import { translateFn } from '../../utils/translator.helper';
import {
  CARD_TYPES,
  DEFAULT_NUMBER_OF_ROWS,
  getIntegrationData,
  GRID_TYPES,
  LICENSE_STATUSES_AVAILABLE,
  LICENSE_STATUSES_INSTALLED,
  MIN_CARD_SIZE_WIDTH,
} from './constants';
import { GzCustomTemplateListWebComponent } from '../../ui/gzCustomTemplateList';
import { CustomTemplateListRequest } from '../../ui/gzCustomTemplateList/types';
import {
  ColumnKeys,
  getAllGridFilters,
  getCompanyFilter,
} from '../integrationsMarketplaceAppContainer/types/filter-data';
import { convertGridFilterToExtJsFilters } from '../../grid/utils/filters';
import { GzDialogWebComponent } from '../../ui/gzDialog';

export function populateData(data: any, gridType: string, userPermissions: any) {
  if (!data) {
    return [];
  }
  let modifiedData = data.map((item: any) => {
    const integrationData = getIntegrationData(item.type);
    return {
      ...item,
      hoverEditable: isHoverEditableCard(gridType),
      historyLogsVisible: canSeeLogs(userPermissions),
      integrationName: gridType === GRID_TYPES.INSTALLED ? item.name : undefined, // Preserve original name as `integrationName`
      readonly: isReadOnlyCard(integrationData),
      status: getLicenseStatus(item.status, gridType, userPermissions, integrationData.cardType),
      statusType: item?.status,
      ...integrationData,
    };
  });

  if (gridType === GRID_TYPES.INSTALLED) {
    modifiedData = modifiedData.map((item: any) => {
      const { specifics } = item;
      const { lastSync, started, stopped } = specifics || {};
      return {
        ...item,
        details: {
          integrationName: item.integrationName,
          lastSync,
          started,
          stopped,
        },
      };
    });
  }

  return modifiedData;
}

export function getLicenseStatus(status: number, gridType: string, userPermissions: any, cardType: CARD_TYPES) {
  if (gridType === GRID_TYPES.AVAILABLE) {
    // check for permissions
    if (userPermissions && !userPermissions.canAddEditIntegrations) {
      return translateFn('Contact your administrator');
    }

    switch (status) {
      case LICENSE_STATUSES_AVAILABLE.NOT_LICENSED:
        return translateFn('License required');
      case LICENSE_STATUSES_AVAILABLE.AVAILABLE:
        return translateFn('Available');
      case LICENSE_STATUSES_AVAILABLE.USAGE_LIMIT_REACHED:
        return translateFn('Usage limit reached');
      default:
        // if status is not set, multiple companies are selected
        return translateFn('Varies by company');
    }
  }

  switch (status) {
    case LICENSE_STATUSES_INSTALLED.STOPPED:
      return translateFn('Stopped');
    case LICENSE_STATUSES_INSTALLED.AVAILABLE:
      return translateFn('Available');
    case LICENSE_STATUSES_INSTALLED.ACTION_REQUIRED:
      return translateFn('Action required');
    case LICENSE_STATUSES_INSTALLED.USAGE_LIMIT_REACHED:
      return translateFn('Usage limit reached');
    case LICENSE_STATUSES_INSTALLED.PENDING:
      return translateFn('Pending');
    case LICENSE_STATUSES_INSTALLED.ACTIVE:
      return translateFn('Active');
    case LICENSE_STATUSES_INSTALLED.ERROR:
      return translateFn('Error');
    default:
      return translateFn('Unknown');
  }
}

export function computeGridAttributes(grid: GzCustomTemplateListWebComponent) {
  const newNumberOfColumns = Math.max(1, Math.floor(grid.clientWidth / MIN_CARD_SIZE_WIDTH));
  const pageSizeOptions = [
    { id: '1', title: `${newNumberOfColumns * 2}`, value: `${newNumberOfColumns * 2}` },
    { id: '2', title: `${newNumberOfColumns * 4}`, value: `${newNumberOfColumns * 4}` },
    { id: '3', title: `${newNumberOfColumns * 8}`, value: `${newNumberOfColumns * 8}` },
  ];
  const newPageSize = newNumberOfColumns * DEFAULT_NUMBER_OF_ROWS;

  return {
    columns: newNumberOfColumns,
    pageSize: newPageSize,
    pageSizeOptions: pageSizeOptions,
  };
}

function isReadOnlyCard(item: any) {
  if (!item || !item.hasOwnProperty('cardType')) {
    return false;
  }
  return item.cardType === CARD_TYPES.READ_ONLY;
}

function canSeeLogs(userPermissions: any) {
  return userPermissions.canAddEditIntegrations === true;
}

function isHoverEditableCard(gridType: string) {
  return gridType === GRID_TYPES.INSTALLED;
}

export function isButtonDisabled(permissions: any, item: any): boolean {
  // status is set for multiple company selection or integration is available
  const notLicensed =
    item.statusType === undefined ||
    item.statusType === LICENSE_STATUSES_AVAILABLE.NOT_LICENSED ||
    item.statusType === LICENSE_STATUSES_INSTALLED.USAGE_LIMIT_REACHED;

  if (Application.Main.company.isPartner() && permissions.canAddEditIntegrations) {
    return false;
  }

  return notLicensed || !permissions.canAddEditIntegrations;
}

export function computeRequestForReadIntegrations(request: CustomTemplateListRequest) {
  // check if filters are empty and set default filters
  if (request.filters === undefined || request.filters.length === 0) {
    request.filters = getAllGridFilters();
  }

  // Extract the category filter
  const categoryFilter = request.filters.find(filter => filter.hasOwnProperty(ColumnKeys.CATEGORY));
  const convertedCategoryFilter = convertGridFilterToExtJsFilters(categoryFilter);

  // Extract the category filter
  const statusFilter = request.filters.find(filter => filter.hasOwnProperty(ColumnKeys.STATUS));
  const convertedStatusFilter = convertGridFilterToExtJsFilters(statusFilter);

  // Extract the company filter and set it as extraParams
  let companyFilter = request.filters.find(filter => filter.hasOwnProperty(ColumnKeys.COMPANY));
  // check if company filter is undefined for Customer
  if (companyFilter === undefined) {
    companyFilter = getCompanyFilter();
  }
  const convertedFilterCompanies = convertGridFilterToExtJsFilters(companyFilter);
  const newExtraParams = {
    companyIds: convertedFilterCompanies ? convertedFilterCompanies[0].value : Application.Main.user.company.id,
    category: convertedCategoryFilter[0].value,
    status: convertedStatusFilter[0].value,
  };

  return {
    ...request,
    filters: [], // remove filters parsed from grid-filter
    extraParams: newExtraParams,
  };
}

export function showCancelWarningDialog(confirmCallback: () => void) {
  const windowBodyHtml = `
      <p>
        ${this.dependencies.translateFn('Changes you have made will be lost.')}
      </p>`;

  const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${this.dependencies.translateFn('Discard')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${this.dependencies.translateFn('Keep changes')}
      </gz-button>`;

  const windowTitle = this.dependencies.translateFn('Discard changes?');

  GzDialogWebComponent.computeCustomDialog(windowBodyHtml, windowFooterHtml, windowTitle, null, confirmCallback);
}
